import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { TooltipContainer } from '../../services/tutorial/models/tooltip-container';
import { TutorialService } from '../../services/tutorial/tutorial.service';

@Component({
  selector: 'bom-tooltip',
  templateUrl: './tooltip.component.html',
  styleUrls: ['./tooltip.component.scss'],
})
export class TooltipComponent implements OnDestroy, OnInit {
  @ViewChild('container')
  containerElement: ElementRef;

  hostElement: ElementRef;
  titleResourceKey: string;
  bodyResourceKey: string;
  showDontShowAgain = false;
  placement: string;
  tutorialSequenceId: string;
  step: number;
  offset = 10;
  tooltip$: Observable<TooltipContainer>;
  initialized$ = new BehaviorSubject<boolean>(false);

  top = -10000;
  left = -10000;

  private componentDestroyed$ = new Subject();
  private containerElementExists$: Observable<boolean>;

  constructor(public tutorialService: TutorialService) {}

  ngOnInit(): void {
    this.tooltip$ = this.tutorialService.currentActiveStep$.pipe(
      map(tooltip =>
        tooltip &&
        tooltip.tutorialSequenceId === this.tutorialSequenceId &&
        tooltip.step === this.step
          ? tooltip
          : null
      )
    );

    // Container element should exist after waiting 200ms
    // 200ms is also the desired delay between tooltips popping up
    this.containerElementExists$ = interval(200).pipe(
      filter(() => this.containerElement && this.containerElement.nativeElement),
      take(1),
      map(() => true)
    );

    this.tooltip$
      .pipe(
        takeUntil(this.componentDestroyed$),
        filter(tooltip => !!tooltip),
        switchMap(_ => this.containerElementExists$)
      )
      .subscribe(() => {
        this.setPosition();
        this.scrollIntoView();
      });
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  scrollIntoView() {
    const htmlHost = this.hostElement.nativeElement as HTMLElement;
    htmlHost.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  onOK() {
    this.tutorialService.triggerNextStepForSequence(this.tutorialSequenceId);
  }

  setDontShowAgain(isChecked: boolean) {
    this.tutorialService.setDontShowAgain(isChecked, this.tutorialSequenceId);
  }

  setPosition() {
    const hostPos = this.hostElement.nativeElement.getBoundingClientRect();
    const tooltipPos = this.containerElement.nativeElement.getBoundingClientRect();

    let top, left;

    const toolHeight = tooltipPos.height;
    const toolWidth = tooltipPos.width;

    if (this.placement === 'top') {
      top = -hostPos.height - toolHeight - this.offset;
      left = 0 + hostPos.width / 2 - toolWidth / 2;
    }

    if (this.placement === 'bottom') {
      top = this.offset;
      left = 0 + hostPos.width / 2 - toolWidth / 2;
    }

    if (this.placement === 'left') {
      top = -toolHeight / 2 - hostPos.height / 2;
      left = 0 - toolWidth - this.offset;
    }

    if (this.placement === 'right') {
      top = -toolHeight / 2 - hostPos.height / 2;
      left = 0 + hostPos.width + this.offset;
    }

    this.top = top;
    this.left = left;
  }
}
